import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

export type TacticoreAuthoringChromeMeta = {
  title: string;
  organizationName: string | null;
  /** Header save status (e.g. Saved / Saving…); set by LessonPlanFormPage when P5b on. */
  saveStatus: string | null;
};

type TacticoreAuthoringChromeContextValue = TacticoreAuthoringChromeMeta & {
  setMeta: (patch: Partial<TacticoreAuthoringChromeMeta>) => void;
  resetMeta: () => void;
};

const DEFAULT_META: TacticoreAuthoringChromeMeta = {
  title: 'Lesson Plan',
  organizationName: null,
  saveStatus: null,
};

const TacticoreAuthoringChromeContext = createContext<TacticoreAuthoringChromeContextValue | null>(
  null,
);

export const TacticoreAuthoringChromeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [meta, setMetaState] = useState<TacticoreAuthoringChromeMeta>(DEFAULT_META);
  const setMeta = useCallback((patch: Partial<TacticoreAuthoringChromeMeta>) => {
    setMetaState((prev) => ({ ...prev, ...patch }));
  }, []);
  const resetMeta = useCallback(() => setMetaState(DEFAULT_META), []);
  const value = useMemo(
    () => ({ ...meta, setMeta, resetMeta }),
    [meta, setMeta, resetMeta],
  );
  return (
    <TacticoreAuthoringChromeContext.Provider value={value}>
      {children}
    </TacticoreAuthoringChromeContext.Provider>
  );
};

export function useTacticoreAuthoringChromeMeta(): TacticoreAuthoringChromeContextValue {
  const ctx = useContext(TacticoreAuthoringChromeContext);
  if (!ctx) {
    return {
      ...DEFAULT_META,
      setMeta: () => undefined,
      resetMeta: () => undefined,
    };
  }
  return ctx;
}
